/**
 * useSessionHandoff — return a child specialist session to the parent that
 * spawned it. Shared by EVERY sub-app that can run as a child (alg protocol
 * runner, yc compatibility, … any future specialist) — one navigation-only
 * primitive, not a per-app copy.
 *
 * The handoff is PULL, not push: the child posts nothing back and is never
 * archived. Returning just navigates back, passing the child session id as
 * `?from_child=<id>` so the PARENT records the child's summary itself — the
 * destination calls POST /sessions/{parent}/summary {child_session_id}, and
 * the server pulls a grounded summary of the child and stores it as an
 * inbound_summary message in the parent (the twin's return value). The child
 * never supplies the text. useRecordChildSummary below is that shared consumer
 * mutation (pages/Session.tsx and Home.tsx both record through it).
 *
 * WHERE the return lands: the launcher appends `?return_to=<its own URL>` to
 * the child-app URL (see RETURN_TO_PARAM), so the return goes back to the
 * exact screen the run was launched from — the Home workspace pane, the
 * full-screen chat, a sub-app parent. Validated open-redirect-safe
 * (getSafeCrossAppReturnTo); absent or invalid, the return falls back to the
 * parent's full-screen session URL (the pre-return_to behaviour, which was a
 * bug whenever the launch DIDN'T happen there). A return_to destination also
 * gets `parent_session=<id>` so a screen without the parent in its path
 * (/home) can still record the summary and re-select the conversation.
 *
 * Cross-origin: a sub-app (alg.host / yc.host / care-planner.host) returns to a parent on
 * the MAIN app host. `mainSessionUrl` strips any sub-app subdomain prefix (and
 * maps a sub-app dev port back to the main port), so this works from every
 * sub-app without per-app host logic — a full-page navigation, not a SPA route.
 *
 * Navigation can't fail the way the old POST could, so there is no
 * pending/error state to surface.
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { handoffApi } from '@/api/endpoints/handoff'
import { errorToast } from '@/hooks/errorToast'
import { useIsFramed } from '@/components/agentDetail/agentSurface'
import { sessionKeys } from '@/hooks/useSessions'
import { useHomeNavStore } from '@/stores/homeNav'
import { MAIN_APP_ROUTES } from '@/lib/routes'
import { getSafeCrossAppReturnTo } from '@/lib/safeReturnTo'
import {
  mainSessionUrl,
  FROM_CHILD_PARAM,
  RETURN_TO_PARAM,
  RETURN_PARENT_PARAM,
} from '@/lib/sessionRouting'

interface ReturnToParentParams {
  /** The parent session to return to (any session type / app). */
  parentSessionId: string
  /** The child being handed back — appended as ?from_child for the live pull. */
  childSessionId: string
}

/**
 * The validated launch-screen URL this child was opened from, read off the
 * child app's own URL (the launcher appended it — algProtocolRunUrl et al).
 * null when absent or not first-party (open-redirect-safe).
 */
export function launchReturnTo(): string | null {
  const raw = new URLSearchParams(window.location.search).get(RETURN_TO_PARAM)
  return getSafeCrossAppReturnTo(raw)
}

/**
 * Absolute URL the child navigates to on return. With a (pre-validated)
 * `returnTo` launch URL: that screen, carrying `from_child` + `parent_session`
 * so it can pull the summary wherever the parent id isn't in its path. Without
 * one: the parent's full-screen MAIN-app session URL carrying `from_child`
 * (parent id already in the path). Pure + exported for testability;
 * `returnTo` must be an absolute URL (launchReturnTo guarantees it).
 */
export function returnToParentUrl(
  parentSessionId: string,
  childSessionId: string,
  returnTo?: string | null,
): string {
  const url = new URL(returnTo ?? mainSessionUrl(parentSessionId))
  url.searchParams.set(FROM_CHILD_PARAM, childSessionId)
  // Only a return_to destination needs the parent named explicitly — the
  // fallback carries it in the /sessions/:id path.
  if (returnTo) url.searchParams.set(RETURN_PARENT_PARAM, parentSessionId)
  return url.href
}

interface UseSessionHandoffResult {
  /** Navigate back to the launch screen (cross-origin full navigation), passing from_child. */
  returnToChat: (params: ReturnToParentParams) => void
}

export function useSessionHandoff(): UseSessionHandoffResult {
  const { t } = useTranslation()
  // A child opened IN the frame returns there too: the parent chat is a pane
  // selection away, and a cross-origin hop would reload the twin to get back
  // to the conversation it never left.
  //
  // The summary pull is done HERE for that path, because the thing that
  // normally triggers it is the `?from_child=` on the returning navigation
  // (useHomeChildReturn) — and there is no navigation. Selecting the parent
  // without it would leave the run's outcome out of the conversation that
  // started it: the same contract, silently unhonoured.
  const framed = useIsFramed()
  const navigate = useNavigate()
  const setSelectedConversationId = useHomeNavStore((s) => s.setSelectedConversationId)
  const recordChildSummary = useRecordChildSummary()
  const returnToChat = useCallback(
    ({ parentSessionId, childSessionId }: ReturnToParentParams): void => {
      if (framed) {
        // Same never-silent contract useHomeChildReturn holds for the
        // navigating return: a failed pull is a toast with a retry, and an
        // access error stays quiet (the run is over either way).
        const record = () =>
          recordChildSummary.mutate(
            { parentSessionId, childSessionId },
            {
              onError: (err) => {
                if (err instanceof ApiError && [401, 403].includes(err.status)) return
                errorToast({
                  context: 'recordChildSummary',
                  description: t('chat.inboundSummary.loadError', 'Could not load the summary.'),
                  onRetry: record,
                })
              },
            },
          )
        record()
        // setSelectedConversationId ALSO clears selectedAgentFace in one
        // set() (see homeNav.ts — the two are mutually exclusive by
        // construction), so this is the one atomic store transition, not a
        // clear-then-select pair: no intermediate render can ever see a
        // cleared face with the OLD conversation id. A face selection would
        // otherwise outrank a conversation selection in Home's own derivation
        // (effectiveConversationId falls back to null whenever
        // effectiveAgentFace is set) — found live verifying this fix: landing
        // on a run via a raw deep-link URL adopts the agent's FACE as pane
        // identity (Home.tsx's deep-link-adoption effect), and returning from
        // that run to a real conversation was then silently swallowed, stuck
        // showing the run, because nothing cleared the face. Only reachable
        // via a raw URL/reload into an agent's own route, not the ordinary
        // in-app tap-through (verified both live) — but every framed return
        // must clear it, or the same trap reopens for the next child.
        setSelectedConversationId(parentSessionId)
        // Selecting is not enough for the URL: the launch PUSHED the run route
        // (/home/a/<slug>/runs/:id), and Home's selection→URL sync effect
        // early-returns when the selection is unchanged — which on a return it
        // always is, because launching never deselected the parent. Left
        // alone, the pane shows the conversation while the address bar, a
        // reload, and the history entry all still hold the finished run — the
        // same dead-selection trap Home.tsx documents on openSessionInPane.
        // Replace (not push) so the finished run does not survive as a
        // history entry either.
        navigate(MAIN_APP_ROUTES.home, { replace: true })
        return
      }
      window.location.href = returnToParentUrl(parentSessionId, childSessionId, launchReturnTo())
    },
    [framed, navigate, recordChildSummary, setSelectedConversationId, t],
  )
  return { returnToChat }
}

interface UseReturnToParentResult {
  /** True when this session has a parent — i.e. it's a child specialist run. */
  isChild: boolean
  /** Navigate back to the parent (no-op when not a child). */
  returnToParent: () => void
}

/**
 * useReturnToParent — the session-aware return primitive EVERY sub-app shares
 * (alg protocol runner, compatibility, … any future child). It takes the
 * session's parent linkage and exposes `isChild` + a `returnToParent` action, so
 * each app stops re-deriving "am I a child?" and re-building the handoff: the
 * contract (carry from_child, navigate back to the launch screen or the main
 * host) lives here, once. Pair with <ReturnToParentButton> for the standard
 * pill, or drive a custom control (alg's header / FHIR-summary CTAs) from
 * {isChild, returnToParent}.
 */
export function useReturnToParent(
  parentSessionId: string | null | undefined,
  childSessionId: string | null | undefined,
): UseReturnToParentResult {
  const { returnToChat } = useSessionHandoff()
  const isChild = !!parentSessionId && !!childSessionId
  const returnToParent = useCallback((): void => {
    if (!parentSessionId || !childSessionId) return
    returnToChat({ parentSessionId, childSessionId })
  }, [parentSessionId, childSessionId, returnToChat])
  return { isChild, returnToParent }
}

interface RecordChildSummaryParams {
  /** The parent that records the summary (POST /sessions/{parent}/summary). */
  parentSessionId: string
  /** The returned child whose live summary the server pulls. */
  childSessionId: string
}

/**
 * useRecordChildSummary — the parent-side consumer of a child's return: asks
 * the server to pull the child's grounded summary and record it as an
 * inbound_summary message on the parent, then refetches the parent so it
 * renders in the timeline (and lands in the twin's LLM context).
 * Parent-initiated, server-pulled, idempotent — the child posts nothing.
 * Shared by every screen a return can land on (pages/Session.tsx, Home.tsx)
 * so the pull contract lives once.
 */
export function useRecordChildSummary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ parentSessionId, childSessionId }: RecordChildSummaryParams) =>
      handoffApi.recordChildSummary(parentSessionId, childSessionId),
    onSuccess: (_data, { parentSessionId }) => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(parentSessionId) })
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() })
    },
  })
}
